import { INSTALL_PROMPT_MAX_VISITS, STORAGE_KEYS } from '@/config/constants';
import { el } from './dom';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function initInstallPrompt(): void {
  if (isStandalone()) return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try {
      localStorage.setItem(STORAGE_KEYS.installDismissed, '1');
    } catch {
      /* ignore */
    }
  });

  const visits = readVisits();
  if (visits >= INSTALL_PROMPT_MAX_VISITS) return;
  writeVisits(visits + 1);

  if (readDismissed()) return;

  // Damos un pequeño respiro al boot antes de mostrar el overlay,
  // por si Chrome todavía no disparó beforeinstallprompt.
  window.setTimeout(() => {
    if (isStandalone() || readDismissed()) return;
    showInstallPrompt();
  }, 1200);
}

function showInstallPrompt(): void {
  const ios = isIOS();
  const canAndroidInstall = !!deferredPrompt;

  if (!ios && !canAndroidInstall) {
    // Otros navegadores donde no podemos detectar instalación: no molestamos.
    return;
  }

  const root = document.getElementById('ui-root');
  if (!root) return;

  const backdrop = el(
    'div',
    {
      class:
        'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm fade-in safe-bottom safe-top px-4'
    },
    []
  );

  const card = el(
    'div',
    {
      class:
        'panel w-full max-w-sm p-6 flex flex-col items-stretch gap-4 mb-2'
    },
    [
      el('div', { class: 'flex items-center gap-3' }, [
        el('div', { class: 'text-3xl' }, ['📲']),
        el('div', { class: 'flex flex-col' }, [
          el('div', { class: 'font-display text-white text-lg' }, ['Añadir a inicio']),
          el('div', { class: 'text-white/60 text-xs' }, [
            'Juega más rápido como una app, en pantalla completa.'
          ])
        ])
      ])
    ]
  );

  if (canAndroidInstall) {
    const installBtn = el('button', { class: 'btn-primary w-full' }, ['Instalar app']);
    installBtn.addEventListener('click', async () => {
      if (!deferredPrompt) return;
      installBtn.setAttribute('disabled', 'true');
      installBtn.innerHTML = 'Abriendo...';
      try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          markDismissedForever();
        }
      } catch (err) {
        console.error('install prompt failed', err);
      }
      deferredPrompt = null;
      close();
    });
    card.append(installBtn);
  } else if (ios) {
    card.append(
      el('div', { class: 'text-white/80 text-sm leading-relaxed bg-bg/40 rounded-xl p-3' }, [
        'En Safari, toca el botón ',
        el('span', { class: 'text-accent font-semibold' }, ['Compartir']),
        ' (cuadrado con flecha hacia arriba) y elige ',
        el('span', { class: 'text-accent font-semibold' }, ['"Añadir a pantalla de inicio"']),
        '.'
      ])
    );
  }

  const laterBtn = el('button', { class: 'btn-ghost w-full' }, ['Ahora no']);
  const dontShowBtn = el(
    'button',
    { class: 'text-white/40 text-xs underline-offset-2 hover:underline mt-1' },
    ['No mostrar de nuevo']
  );

  const close = () => {
    backdrop.remove();
  };

  laterBtn.addEventListener('click', close);
  dontShowBtn.addEventListener('click', () => {
    markDismissedForever();
    close();
  });

  card.append(laterBtn, dontShowBtn);
  backdrop.append(card);

  // Cerrar al tocar el backdrop (pero no la card).
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  root.append(backdrop);
}

function isStandalone(): boolean {
  const mq = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone =
    typeof navigator !== 'undefined' &&
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mq || iosStandalone;
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

function readVisits(): number {
  try {
    return Number(localStorage.getItem(STORAGE_KEYS.installVisits) ?? '0') || 0;
  } catch {
    return 0;
  }
}

function writeVisits(n: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.installVisits, String(n));
  } catch {
    /* ignore */
  }
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS.installDismissed) === '1';
  } catch {
    return false;
  }
}

function markDismissedForever(): void {
  try {
    localStorage.setItem(STORAGE_KEYS.installDismissed, '1');
  } catch {
    /* ignore */
  }
}
